import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";

type Row = { calls: number; totalTokens: number; estimatedCostUsd: number };
type RestaurantRow = Row & { restaurantId: number; restaurantName: string };
type UserRow = Row & { userId: number | null; userEmail: string | null };
type Failure = { createdAt: string; restaurantName: string; userEmail: string | null; feature: string; error: string };
type Data = {
  range: string;
  totals: Row & { promptTokens: number; completionTokens: number };
  byRestaurant: RestaurantRow[];
  byUser: UserRow[];
  failures: { count: number; recent: Failure[] };
};

const RANGES = ["all", "today", "week", "month"] as const;

const fmtUsd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;
const fmtNum = (n: number) => n.toLocaleString();

/**
 * Global + per-restaurant + per-user Gemini token usage and estimated cost
 * (Fred, 2026-09-18: "watch AI usage before we bust our tokens"). Superadmin
 * only. Purely a read of AiUsageLog rows already written at call time --
 * never calls Gemini or Google Cloud itself.
 */
const AiUsage: React.FC = () => {
  const { t } = useTranslation();
  const [range, setRange] = useState<(typeof RANGES)[number]>("month");
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/superadmin/ai-usage?range=${range}`);
      if (!res || !res.ok) {
        setError(t("aiUsage.failedToLoad"));
        return;
      }
      setData(await res.json());
    } catch {
      setError(t("aiUsage.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [range, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">{t("aiUsage.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("aiUsage.subtitle")}</p>
        </div>
        <select
          className="border rounded p-2 text-sm"
          value={range}
          onChange={(e) => setRange(e.target.value as (typeof RANGES)[number])}
        >
          <option value="all">{t("aiUsage.range.all")}</option>
          <option value="today">{t("aiUsage.range.today")}</option>
          <option value="week">{t("aiUsage.range.week")}</option>
          <option value="month">{t("aiUsage.range.month")}</option>
        </select>
      </div>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      {loading && !data ? (
        <div className="text-gray-500">{t("common.loading")}</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-xs text-gray-500">{t("aiUsage.calls")}</div>
              <div className="text-2xl font-bold text-gray-900">{fmtNum(data.totals.calls)}</div>
            </div>
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-xs text-gray-500">{t("aiUsage.totalTokens")}</div>
              <div className="text-2xl font-bold text-gray-900">{fmtNum(data.totals.totalTokens)}</div>
              <div className="text-xs text-gray-400 mt-0.5">
                {t("aiUsage.promptCompletionSplit", {
                  prompt: fmtNum(data.totals.promptTokens),
                  completion: fmtNum(data.totals.completionTokens),
                })}
              </div>
            </div>
            <div className="col-span-2 lg:col-span-1 bg-white rounded-xl shadow p-4">
              <div className="text-xs text-gray-500">{t("aiUsage.estimatedCost")}</div>
              <div className="text-2xl font-bold text-teal-600">{fmtUsd(data.totals.estimatedCostUsd)}</div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-6">{t("aiUsage.costCaveat")}</p>

          <div className="mb-6">
            <h2 className="font-semibold text-gray-700 mb-2">
              {t("aiUsage.failures.title", { count: data.failures.count })}
            </h2>
            {data.failures.count === 0 ? (
              <p className="text-sm text-gray-400">{t("aiUsage.failures.none")}</p>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.failures.when")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.col.restaurant")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.col.user")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.failures.button")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.failures.reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.failures.recent.map((f, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{new Date(f.createdAt).toLocaleString()}</td>
                        <td className="px-3 py-2 text-gray-700">{f.restaurantName}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-[9rem] truncate" title={f.userEmail || undefined}>
                          {f.userEmail || t("aiUsage.unknownUser")}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {f.feature === "review_reply_correct" ? t("aiUsage.failures.correct") : t("aiUsage.failures.suggest")}
                        </td>
                        <td className="px-3 py-2 text-amber-700">
                          {f.error === "timeout" ? t("aiUsage.failures.timeout")
                            : ["429", "500", "502", "503", "504"].includes(f.error) ? t("aiUsage.failures.busy", { code: f.error })
                            : t("aiUsage.failures.other", { code: f.error })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.failures.count > data.failures.recent.length && (
                  <p className="text-xs text-gray-400 px-3 py-2 border-t">
                    {t("aiUsage.failures.showingLatest", { shown: data.failures.recent.length })}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h2 className="font-semibold text-gray-700 mb-2">{t("aiUsage.byRestaurant")}</h2>
              <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.col.restaurant")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500 text-right">{t("aiUsage.col.calls")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500 text-right">{t("aiUsage.col.tokens")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500 text-right">{t("aiUsage.col.cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byRestaurant.length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">{t("aiUsage.empty")}</td></tr>
                    ) : data.byRestaurant.map((r) => (
                      <tr key={r.restaurantId} className="border-t">
                        <td className="px-3 py-2 text-gray-700">{r.restaurantName}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.calls)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.totalTokens)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(r.estimatedCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="font-semibold text-gray-700 mb-2">{t("aiUsage.byUser")}</h2>
              <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-500">{t("aiUsage.col.user")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500 text-right">{t("aiUsage.col.calls")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500 text-right">{t("aiUsage.col.tokens")}</th>
                      <th className="px-3 py-2 font-medium text-gray-500 text-right">{t("aiUsage.col.cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byUser.length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">{t("aiUsage.empty")}</td></tr>
                    ) : data.byUser.map((u, i) => (
                      <tr key={u.userId ?? u.userEmail ?? i} className="border-t">
                        <td className="px-3 py-2 text-gray-700 max-w-[9rem] truncate" title={u.userEmail || undefined}>
                          {u.userEmail || t("aiUsage.unknownUser")}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(u.calls)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(u.totalTokens)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(u.estimatedCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AiUsage;
